import type {GenderDTO} from '@/ts/types/dto/gender-dto';
import type countryNames from 'i18n-iso-countries/langs/en.json';
import type {Language} from '@/ts/types/component/localization.type';

/** ISO date string in yyyy-MM-dd format */
export type StringDateDTO = `${number}-${number}-${number}`;
/** UUID string in the standard 8-4-4-4-12 format */
export type UuidDTO = `${string}-${string}-${string}-${string}-${string}`;
/** ISO 3166-1 alpha-2 country code */
export type CountryCode = keyof typeof countryNames.countries;

/** Request payload for creating a new user account */
export interface SignUpRequest {
  email: string;
  name: string;
  gender: GenderDTO;
  educationLevel: EducationLevelDTO;
  termsAndConditionsId: UuidDTO;
  privacyPolicyId: UuidDTO;
  marketingCode?: string;
  birthYear: number;
  captchaToken: string|null;
  languageCode: Language;
  analyticsOptIn: boolean;
  researchOptIn: boolean;
  analyticsAnonymizedOptIn: boolean;
  voucherCode?: string;
}

/**
 * Request payload for completing sign-up for an identity that already exists in Zitadel
 * (e.g. signed in with Google). `email` comes from the authenticated user and `captchaToken`
 * is dropped — the Zitadel session already proves a human.
 */
export type CompleteSignUpRequest = Omit<SignUpRequest, 'email' | 'captchaToken'>;

/** Whether the authenticated Zitadel identity already has a Balm account */
export interface RegistrationStatusResponse {
  registered: boolean;
}


/** Education level options available during user sign-up */
export enum EducationLevelDTO {
  SOME_HIGH_SCHOOL = 'some_high_school',
  HIGH_SCHOOL_DIPLOMA = 'high_school_diploma',
  BACHELORS_DEGREE = 'bachelors_degree',
  MASTERS_DEGREE = 'masters_degree',
  PHD_OR_HIGHER = 'phd_or_higher',
  TRADE_SCHOOL = 'trade_school',
  PREFER_NOT_TO_SAY = 'prefer_not_to_say',
}

/** Request to validate an organization voucher code during sign-up */
export interface ValidateVoucherRequest {
  voucherCode: string;
}

/**
 * Wire-format identifiers the BE uses inside `blacklistedFeatures` arrays
 * (on voucher-validation responses today, possibly licenses in the future).
 * Distinct from the post-login `Feature` enum in `useFeatureAccess` because
 * these are public-payload concepts, not authenticated-license gating.
 */
export enum BlacklistedFeature {
  CAPTCHA = 'CAPTCHA',
}

/** Result of voucher validation including validity, failure reason, and associated organization */
export interface ValidateVoucherResponse {
  valid: boolean;
  failureReason?: string;
  organization?: OrganizationDTO;
  blacklistedFeatures?: BlacklistedFeature[];
}

/** An organization that provides licenses to its members via voucher codes */
export interface OrganizationDTO {
  id: UuidDTO;
  name: string;
  createDate: string;
  licenses: LicenseDTO[];
}

/** A license granting platform access for a defined period with optional feature restrictions */
export interface LicenseDTO {
  id: UuidDTO;
  name: string;
  createDate: string;
  validityPeriodInDays: number;
  provisionType: LicenseProvisionTypeDTO;
  blacklistedFeatures?: string[];
}

/** Methods by which a license can be provisioned to a user */
export enum LicenseProvisionTypeDTO {
  VOUCHER_BASED = 'voucher_based',
  DIRECT_PAYMENT = 'direct_payment',
  GIFTED = 'gifted',
  INSURANCE_BACKED = 'insurance_backed',
}